import type {
  AcquireRequest,
  ActivityDTO,
  CatalogItemDTO,
  ConnectedServiceDTO,
  InviteUserRequest,
  MeDTO,
  SubscriptionDTO,
  UpdateSubscriptionRequest,
  UserDTO,
  WorkspaceOptionDTO,
} from "@shared/api";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: "same-origin",
    ...init,
    headers: {
      "content-type": "application/json",
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      message = body.message ?? body.error ?? message;
    } catch {
      /* swallow JSON parse errors on non-JSON responses */
    }
    throw new ApiError(message, res.status);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  auth: {
    me: () => request<MeDTO>("/auth/me"),
    workspaces: () => request<WorkspaceOptionDTO[]>("/auth/workspaces"),
    login: (email: string, password: string) =>
      request<MeDTO>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      }),
    logout: () => request<void>("/auth/logout", { method: "POST" }),
  },
  subscriptions: {
    list: () => request<SubscriptionDTO[]>("/subscriptions"),
    get: (id: string) => request<SubscriptionDTO>(`/subscriptions/${id}`),
    update: (id: string, body: UpdateSubscriptionRequest) =>
      request<SubscriptionDTO>(`/subscriptions/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    setServices: (id: string, services: string[]) =>
      request<SubscriptionDTO>(`/subscriptions/${id}/services`, {
        method: "PUT",
        body: JSON.stringify({ services }),
      }),
    acquire: (body: AcquireRequest) =>
      request<SubscriptionDTO>("/subscriptions", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },
  services: {
    list: () => request<ConnectedServiceDTO[]>("/services"),
  },
  catalog: {
    list: () => request<CatalogItemDTO[]>("/catalog"),
  },
  users: {
    list: () => request<UserDTO[]>("/users"),
    invite: (body: InviteUserRequest) =>
      request<UserDTO>("/users", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    assignSeat: (userId: string, subscriptionId: string) =>
      request<void>(`/users/${userId}/seats/${subscriptionId}`, {
        method: "POST",
      }),
    removeSeat: (userId: string, subscriptionId: string) =>
      request<void>(`/users/${userId}/seats/${subscriptionId}`, {
        method: "DELETE",
      }),
  },
  activity: {
    list: (limit = 12) =>
      request<ActivityDTO[]>(`/activity?limit=${limit}`),
  },
};
